/**
 * The AI ad generator's URL and its name in each language, for the
 * one-link footers that point at it: the homepage footer (LandingV2), the
 * footer of the pages in the homepage design (CreativeLandingShell: the free
 * tools, the music video generator, the creative tools), and the homepage's
 * crawler HTML (server/_core/seo.ts).
 *
 * Oct 11 2026: no top-traffic page linked the ad page in its crawler HTML
 * (the homepage, the music video generator, the jingle and image generators,
 * the free audio tools). Each name is the ad page's own localized h1
 * (shared/creativeToolsSeoLocales; a test keeps them equal), copied here so
 * the homepage bundle does not pull in the creative-tools copy. Renderers add
 * the language prefix (/es/ai-video-ad-generator).
 *
 * Oct 29 2026: the page leads with "AI ad generator" (it makes image ads and
 * talking-head ads too), so the names did as well.
 */

export const AD_GENERATOR_PATH = "/ai-video-ad-generator";

export const AD_GENERATOR_LINK_LABELS = {
  en: "AI ad generator",
  es: "Generador de anuncios con IA",
  ja: "AI広告メーカー",
  pt: "Gerador de anúncios com IA",
  fr: "Générateur de pubs IA",
  zh: "AI 广告生成器",
  ko: "AI 광고 생성기",
  id: "Pembuat iklan AI",
  it: "Generatore di annunci AI",
  nl: "AI-advertentie generator",
  pl: "Generator reklam AI",
  tr: "Yapay zeka reklam oluşturucu",
  vi: "Tạo quảng cáo bằng AI",
  de: "KI-Werbegenerator",
} as const;

export type AdGeneratorLinkLang = keyof typeof AD_GENERATOR_LINK_LABELS;

/** The ad generator's name in `lang`; English for a language Solmi doesn't serve. */
export function adGeneratorLinkLabel(lang: string): string {
  return (
    AD_GENERATOR_LINK_LABELS[lang as AdGeneratorLinkLang] ??
    AD_GENERATOR_LINK_LABELS.en
  );
}

/**
 * The other ad pages (CREATIVE_AD_PAGE_KEYS after the ad generator, in that
 * order), linked from the same footers in one "Ad tools" row
 * (AdToolsFooterLinks; the homepage crawler HTML carries the row too).
 * Oct 29 2026: only the ad generator was in a footer, so the rest had no
 * link from the homepage or the music video generator. Each name is that
 * page's own localized h1 (a test keeps them equal), copied here for the
 * same bundle reason.
 */
export const AD_TOOL_FOOTER_PAGES = [
  {
    path: "/ai-ugc-video-generator",
    labels: {
      en: "AI UGC video generator",
      es: "Generador de videos UGC con IA",
      ja: "AI UGC動画生成ツール",
      pt: "Gerador de vídeos UGC com IA",
      fr: "Générateur de vidéos UGC IA",
      zh: "AI UGC 视频生成器",
      ko: "AI UGC 영상 생성기",
      id: "Pembuat video UGC AI",
      it: "Generatore di video UGC AI",
      nl: "AI UGC-video generator",
      pl: "Generator wideo UGC AI",
      tr: "Yapay zeka UGC video oluşturucu",
      vi: "Tạo video UGC bằng AI",
      de: "KI-UGC-Video-Generator",
    },
  },
  {
    path: "/ai-product-video-generator",
    labels: {
      en: "AI product video generator",
      es: "Generador de videos de productos con IA",
      ja: "AI商品動画作成ツール",
      pt: "Gerador de vídeo de produto com IA",
      fr: "Générateur de vidéo produit IA",
      zh: "AI 产品视频生成器",
      ko: "AI 제품 영상 생성기",
      id: "Pembuat video produk AI",
      it: "Generatore di video prodotto AI",
      nl: "AI productvideo generator",
      pl: "Generator filmów produktowych AI",
      tr: "Yapay zeka ürün videosu oluşturucu",
      vi: "Tạo video sản phẩm bằng AI",
      de: "KI-Produktvideo-Generator",
    },
  },
  {
    path: "/ai-commercial-generator",
    labels: {
      en: "AI commercial generator",
      es: "Generador de comerciales con IA",
      ja: "AI CM作成ツール",
      pt: "Gerador de comerciais com IA",
      fr: "Générateur de spot publicitaire IA",
      zh: "AI 广告片生成器",
      ko: "AI CF 생성기",
      id: "Pembuat iklan komersial AI",
      it: "Generatore di spot pubblicitari AI",
      nl: "AI reclamespot generator",
      pl: "Generator spotów reklamowych AI",
      tr: "Yapay zeka reklam filmi oluşturucu",
      vi: "Tạo TVC quảng cáo bằng AI",
      de: "KI-Werbespot-Generator",
    },
  },
  {
    path: "/ai-facebook-ad-generator",
    labels: {
      en: "AI Facebook ad generator",
      es: "Generador de anuncios de Facebook con IA",
      ja: "AI Facebook広告作成ツール",
      pt: "Gerador de anúncios para Facebook com IA",
      fr: "Générateur de pub Facebook IA",
      zh: "AI Facebook 广告生成器",
      ko: "AI 페이스북 광고 생성기",
      id: "Pembuat iklan Facebook AI",
      it: "Generatore di annunci Facebook AI",
      nl: "AI Facebook-advertentie generator",
      pl: "Generator reklam na Facebooku AI",
      tr: "Yapay zeka Facebook reklam oluşturucu",
      vi: "Tạo quảng cáo Facebook bằng AI",
      de: "KI-Facebook-Ads-Generator",
    },
  },
  {
    path: "/creative-tools/ai-product-photography",
    labels: {
      en: "AI product photography",
      es: "Fotografía de producto con IA",
      ja: "AI商品撮影",
      pt: "Fotografia de produto com IA",
      fr: "Photo produit IA",
      zh: "AI 产品摄影",
      ko: "AI 제품 사진 만들기",
      id: "Foto produk AI",
      it: "Foto prodotto AI",
      nl: "AI productfotografie",
      pl: "Fotografia produktowa AI",
      tr: "Yapay zeka ürün fotoğrafçılığı",
      vi: "Chụp ảnh sản phẩm bằng AI",
      de: "KI-Produktfotografie",
    },
  },
] as const;

/** Every ad page, the ad generator first: the row hides on these, which carry "More ad tools". */
export const AD_PAGE_PATHS: readonly string[] = [
  AD_GENERATOR_PATH,
  ...AD_TOOL_FOOTER_PAGES.map((page) => page.path),
];

export const AD_TOOLS_FOOTER_HEADINGS = {
  en: "Ad tools",
  es: "Herramientas de anuncios",
  ja: "広告ツール",
  pt: "Ferramentas de anúncios",
  fr: "Outils publicitaires",
  zh: "广告工具",
  ko: "광고 도구",
  id: "Alat iklan",
  it: "Strumenti per annunci",
  nl: "Advertentietools",
  pl: "Narzędzia reklamowe",
  tr: "Reklam araçları",
  vi: "Công cụ quảng cáo",
  de: "Werbe-Tools",
} as const;

/** The "Ad tools" row's heading and links in `lang` (English for a language Solmi doesn't serve). */
export function adToolsFooterRow(lang: string) {
  const code = (lang in AD_TOOLS_FOOTER_HEADINGS ? lang : "en") as AdGeneratorLinkLang;
  return {
    heading: AD_TOOLS_FOOTER_HEADINGS[code],
    links: AD_TOOL_FOOTER_PAGES.map((page) => ({ path: page.path, label: page.labels[code] })),
  };
}
